import axios from 'axios';
import i18next from 'i18next';
import { BASE_URL } from '../constants/endpoints';
import { useStore } from '../store';
import { USER_MESSAGE_TYPES } from '../flows/UserMessageFlow/constants';
import { happyDollarIcon } from '../assets/svg';
import { IServerErrorResponse } from '../types';

const { t } = i18next;

export const httpService = axios.create({
  withCredentials: true,
  baseURL: BASE_URL,
});

const ERROR_CODES = {
  TOKEN_EXPIRED: 401,
  USER_DIFF: 40021,
};

httpService.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { setTokenStatus, setSummary, setMessageObject } = useStore.getState();
    const errCode = error.response.status;
    if (errCode === ERROR_CODES.TOKEN_EXPIRED) {
      const innerErrCode = error.response.data?.errors?.[0]?.code;
      setSummary({});
      localStorage.clear();
      if (innerErrCode === ERROR_CODES.USER_DIFF) {
        setMessageObject({
          pictureClassName: 'w-[84px] h-[84px] mb-[24px]',
          containerClassName: 'w-[400px] h-[400px] py-[30px] px-[30px]',
          type: USER_MESSAGE_TYPES.POP_UP,
          messageTitle: t('GENERAL_ERROR.TITLE'),
          titleClassName: 'text-base leading-[27px] font-metricMedium text-bw08 mb-[21px]',
          messageText: t('GENERAL_ERROR.USER_DIFF'),
          messageTextClassName: 'font-metric text-base text-bw07 leading-[27px] mb-[60px]',
          iconSource: happyDollarIcon,
          mainButtonText: t('GENERAL_ERROR.CTA'),
          buttonClassName:
            'w-[240px] h-[50px] flex items-center justify-center rounded-umd bg-action03 hover:bg-action04 font-metricMedium text-bw00 text-[14px] leading-[25px]',
          callback: () => setMessageObject({ type: USER_MESSAGE_TYPES.ZERO_STATE }),
        });
      } else {
        setTokenStatus(true);
      }
    }
    return Promise.reject(error);
  },
);

const generateEmptyErrorResponse = (code: number): IServerErrorResponse => ({
  response: {
    data: {
      errors: [],
      httpStatus: code,
      reasonPhrase: '',
    },
    status: code,
  },
} as IServerErrorResponse);

interface RequestData {
  [key: string]: any;
}

export const openApiHandler = async <T extends RequestData> (url: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE', data?: T) => {
  const response = await fetch(url, {
    method,
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    if (response.status === 401) {
      // eslint-disable-next-line no-throw-literal
      throw generateEmptyErrorResponse(401);
    } else if (response.status === 500) {
      // eslint-disable-next-line no-throw-literal
      throw generateEmptyErrorResponse(500);
    }
    const errObj = await response.json();
    /** Recreate expected error object as previously expected (IServerErrorResponse) */
    // eslint-disable-next-line no-throw-literal
    throw {
      response: {
        data: {
          errors: errObj?.errors || [],
          httpStatus: errObj?.httpStatus || 500,
          reasonPhrase: errObj?.reasonPhrase || '',
        },
        status: errObj.httpStatus || 500,
      },
    };
  }
  return response.json();
};
